import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ArrowLeft, CheckCircle2, Clock, XCircle } from 'lucide-react';
import clsx from 'clsx';
import { apiRequest, ApiError } from '../lib/api';
import { ListShell } from '../components/ListShell';
import { Skeleton } from '../components/ui/Skeleton';
import { GatedSection } from '../components/shared/GatedSection';
import { formatDate } from '../lib/format';
import {
  AttemptStatusChip,
  ModeChip,
  PassFailChip,
  QuestionRenderer,
  ScopeChip,
  type AttemptDetail,
} from '../components/assessments';
import { GameSharePopup } from '../components/games';

export const Route = createFileRoute(
  '/_authed/assessments/attempts/$attemptId/result',
)({
  component: AttemptResultPage,
});

function AttemptResultPage() {
  const { attemptId } = Route.useParams();

  const detailQuery = useQuery({
    queryKey: ['assessments', 'attempt', attemptId] as const,
    queryFn: ({ signal }) =>
      apiRequest<AttemptDetail>(
        `/api/assessments/attempts/${encodeURIComponent(attemptId)}`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 1;
    },
  });

  return (
    <ListShell
      title="Your result"
      primaryAction={
        <Link
          to="/me/assessments"
          className="inline-flex items-center gap-1 text-sm text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
        >
          <ArrowLeft size={14} aria-hidden="true" />
          Back to my tests & quizzes
        </Link>
      }
    >
      <GatedSection
        error={detailQuery.error}
        title="Result"
        onRetry={() => detailQuery.refetch()}
      >
        {detailQuery.isLoading || !detailQuery.data ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <ResultPanel detail={detailQuery.data} />
        )}
      </GatedSection>
    </ListShell>
  );
}

function ResultPanel({ detail }: { detail: AttemptDetail }) {
  const inProgress = detail.status === 'in_progress';
  const showScore = detail.scorePercent !== null;
  const hasReview = detail.answers.some(
    (a) => a.correctOptionIds && a.correctOptionIds.length > 0,
  );

  // Module 2 hook: when the finished attempt is a weekly or monthly quiz,
  // auto-open the share popup. The popup is fully self-contained — it
  // fetches the student's referral code, builds the share URL, and posts
  // share events with mount-scoped idempotency. We mount it conditionally
  // on scope so the hiring/rules/interview-script flows are untouched.
  const isShareableQuiz =
    !inProgress &&
    (detail.scope === 'weekly_quiz' || detail.scope === 'monthly_quiz');
  const [shareOpen, setShareOpen] = useState(false);
  useEffect(() => {
    if (isShareableQuiz) setShareOpen(true);
  }, [isShareableQuiz]);

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Hero */}
      <section className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-6 text-center shadow-sm">
        <div className="mb-3 flex flex-wrap items-center justify-center gap-2">
          <ScopeChip scope={detail.scope} />
          <ModeChip mode={detail.mode} />
          <AttemptStatusChip status={detail.status} />
          {detail.autoSubmitted ? (
            <span className="rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-xs text-[color:var(--color-text-muted)]">
              Auto-submitted
            </span>
          ) : null}
        </div>
        <h2 className="font-display text-2xl font-bold text-[color:var(--color-text)]">
          {detail.run.title}
        </h2>
        {inProgress ? (
          <p className="mt-3 text-sm text-[color:var(--color-text-muted)]">
            This attempt is still in progress.
          </p>
        ) : (
          <>
            {showScore ? (
              <div className="mt-6 flex items-center justify-center gap-4">
                <div
                  className={clsx(
                    'flex h-32 w-32 items-center justify-center rounded-full border-4',
                    detail.pass === true
                      ? 'border-[color:var(--color-success)] bg-[color:var(--color-success)]/10'
                      : detail.pass === false
                        ? 'border-[color:var(--color-danger)] bg-[color:var(--color-danger)]/10'
                        : 'border-[color:var(--color-border)] bg-[color:var(--color-surface)]',
                  )}
                >
                  <span className="font-display text-3xl font-bold tabular-nums text-[color:var(--color-text)]">
                    {detail.scorePercent}%
                  </span>
                </div>
                <div className="text-left">
                  <p className="font-display text-sm uppercase tracking-wide text-[color:var(--color-text-muted)]">
                    Score
                  </p>
                  <p className="font-display text-xl font-semibold">
                    {detail.correctCount ?? '—'} / {detail.answeredCount ?? '—'} correct
                  </p>
                  <div className="mt-1">
                    <PassFailChip pass={detail.pass} />
                  </div>
                </div>
              </div>
            ) : (
              <p className="mt-3 text-sm text-[color:var(--color-text-muted)]">
                Your test has been recorded. The academy will share the
                outcome when ready.
              </p>
            )}
            <p className="mt-4 inline-flex items-center gap-1 text-xs text-[color:var(--color-text-muted)]">
              <Clock size={12} aria-hidden="true" />
              {detail.submittedAt
                ? `Submitted on ${formatDate(detail.submittedAt, { withTime: true })}`
                : detail.startedAt
                  ? `Started on ${formatDate(detail.startedAt, { withTime: true })}`
                  : null}
            </p>
          </>
        )}
      </section>

      {/* Per-question review */}
      {hasReview && !inProgress ? (
        <section className="space-y-4">
          <header>
            <h3 className="font-display text-lg font-semibold">Review</h3>
            <p className="text-sm text-[color:var(--color-text-muted)]">
              Your answer vs. the correct answer for every question.
            </p>
          </header>

          <ol className="space-y-6">
            {detail.answers.map((a) => (
              <li
                key={a.id}
                className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5"
              >
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className="rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-xs font-semibold tabular-nums text-[color:var(--color-text-muted)]">
                    Q{a.questionPosition}
                  </span>
                  {a.isCorrect === true ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-[color:var(--color-success)]/40 bg-[color:var(--color-success)]/10 px-2 py-0.5 text-xs font-medium text-[color:var(--color-success)]">
                      <CheckCircle2 size={12} aria-hidden="true" /> Correct
                    </span>
                  ) : a.isCorrect === false ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-[color:var(--color-danger)]/40 bg-[color:var(--color-danger)]/10 px-2 py-0.5 text-xs font-medium text-[color:var(--color-danger)]">
                      <XCircle size={12} aria-hidden="true" /> Wrong
                    </span>
                  ) : (
                    <span className="rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-xs text-[color:var(--color-text-muted)]">
                      Not graded
                    </span>
                  )}
                  {typeof a.pointsAwarded === 'number' ? (
                    <span className="text-xs text-[color:var(--color-text-muted)]">
                      {a.pointsAwarded} pt{a.pointsAwarded === 1 ? '' : 's'}
                    </span>
                  ) : null}
                </div>
                <QuestionRenderer
                  position={a.questionPosition}
                  totalPositions={detail.answers.length}
                  stem={a.stem}
                  options={a.options}
                  selectedOptionIds={a.selectedOptionIds ?? []}
                  multiSelect={false}
                  correctOptionIds={a.correctOptionIds ?? []}
                  disabled
                />
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      {isShareableQuiz ? (
        <GameSharePopup
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          attemptId={detail.id}
          scorePercent={detail.scorePercent}
          quizTitle={detail.run.title}
        />
      ) : null}
    </div>
  );
}
